// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import { IconMemo } from "@insite/mobius/Icon";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Close from "@store/Library/Components/Toolkit/Icons/Close";
import { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import TwoButtonModal from "@store/Overrides/Components/TwoButtonModal";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import StepFormProvinceSelect from "@store/Library/Components/Bunzl/StepFormComponents/StepFormProvinceSelect";
import StepFormTextField from "@store/Library/Components/Bunzl/StepFormComponents/StepFormTextField";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import CardComponent from "@store/Library/Widgets/Card/Card";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";
import { useCreditFormContext } from "../../../Widgets/CreditForm/CreditFormContextContainer";
import { TradeReference } from "../../../Widgets/CreditForm/useCreditFormTradeReferences";

interface CreditFormTradeReferencesProps {
    editable?: boolean;
    index: number;
    onDeleteClick: () => void;
    onInputChange: (key: keyof TradeReference, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    tradeReference: TradeReference;
    canDelete?: boolean;
}
interface CreditFormTradeReferenceStyles {
    cardComponentStyles?: InjectableCss;
    removeProductLineIcon?: IconPresentationProps;
    deleteButton?: InjectableCss;
    labelTypography?: TypographyProps;
}

const indexToLabelArray = ["First", "Second", "Third", "Fourth", "Fifth"];

const styles: CreditFormTradeReferenceStyles = {
    labelTypography: {
        css: css`
            width: 100%;
            color: ${getColor("secondary")};
        `,
        variant: "h5",
    },
    deleteButton: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
        `,
    },
    cardComponentStyles: {
        css: css`
            display: flex;

            width: 100%;
            flex-wrap: wrap;
            gap: ${pxToRem(20)};

            position: relative;
        `,
    },
    removeProductLineIcon: {
        src: Close,
        size: 16,
        color: "danger",
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: ${pxToRem(36)};
            height: ${pxToRem(36)};
            border-radius: 50%;
            background-color: ${getColor("common.accent")};
            transition: ${transitionHoverStyles("color")};

            &:hover {
                background-color: ${getColor("common.border")};
            }
        `,
    },
};
const CreditFormTradeReference: React.FC<CreditFormTradeReferencesProps> = ({
    index,
    onDeleteClick,
    onInputChange,
    tradeReference,
    editable = true,
    canDelete = true,
}) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isDeleted, setIsDeleted] = React.useState(false);
    const { validationDictionary } = useCreditFormContext();
    const handleDeleteClick = () => {
        setIsDeleted(true);
        // this timeout is to allow the fade out animation to complete before removing the element
        setTimeout(onDeleteClick, 300);
        setIsModalOpen(false);
    };
    return (
        <FadeIn unmountOnExit={false} appear animate={!isDeleted} duration={index === 0 ? 0 : 100}>
            <CardComponent size="large" variant="elevated" direction="row" extendedStyles={styles.cardComponentStyles}>
                <Typography {...styles.labelTypography}>{`${translate(indexToLabelArray?.[index] || "")} ${translate(
                    "Trade Reference",
                )}`}</Typography>

                {editable && (
                    <Clickable
                        {...styles.deleteButton}
                        disabled={!canDelete}
                        onClick={() => setIsModalOpen(true)}
                        data-test-selector="removeRestricted"
                    >
                        <IconMemo {...styles.removeProductLineIcon} />
                        <VisuallyHidden>{translate("Removed this product")}</VisuallyHidden>
                    </Clickable>
                )}

                <StepFormTextField
                    onChange={event => onInputChange("referenceName", event)}
                    label="Reference Name"
                    required
                    valueOverride={tradeReference.referenceName.value}
                    placeholder="Name of Reference"
                    inputKey="referenceName"
                    validation={validationDictionary["referenceName"]}
                />
                <StepFormTextField
                    required
                    onChange={event => onInputChange("referenceAddress", event)}
                    valueOverride={tradeReference.referenceAddress.value}
                    label="Address"
                    placeholder="Enter Street Address"
                    inputKey="referenceAddress"
                    validation={validationDictionary["address"]}
                />

                <StepFormTextField
                    required
                    onChange={event => onInputChange("referenceCity", event)}
                    label="City"
                    valueOverride={tradeReference.referenceCity.value}
                    placeholder="Enter City"
                    inputKey="referenceCity"
                    variant="small"
                    validation={validationDictionary["city"]}
                />
                <StepFormProvinceSelect
                    onChange={event => onInputChange("referenceProvince", event)}
                    label="Province"
                    valueOverride={tradeReference.referenceProvince.value}
                    inputKey="referenceProvince"
                    required
                    validation={validationDictionary["province"]}
                />
                <StepFormTextField
                    required
                    onChange={event => onInputChange(`referencePostalCode`, event)}
                    label="Postal Code"
                    valueOverride={tradeReference.referencePostalCode.value}
                    placeholder="Enter Postal Code"
                    inputKey="referencePostalCode"
                    variant="small"
                    validation={validationDictionary["postalCode"]}
                />
                <StepFormTextField
                    required
                    onChange={event => onInputChange("referenceContactName", event)}
                    label="Contact Name"
                    valueOverride={tradeReference.referenceContactName.value}
                    placeholder="Contact Name"
                    inputKey="referenceContactName"
                    variant="small"
                    validation={validationDictionary["contactName"]}
                />
                <StepFormTextField
                    required
                    onChange={event => onInputChange(`referencePhoneNumber`, event)}
                    inputKey="referencePhoneNumber"
                    label="Phone Number"
                    placeholder="Enter Phone Number"
                    autoComplete="tel"
                    valueOverride={tradeReference.referencePhoneNumber.value}
                    type="tel"
                    variant="small"
                    validation={validationDictionary["phoneNumber"]}
                />
                <StepFormTextField
                    required
                    variant="small"
                    autoComplete="email"
                    onChange={event => onInputChange("referenceEmail", event)}
                    label="Email Address"
                    valueOverride={tradeReference.referenceEmail.value}
                    placeholder="Enter Email Address"
                    inputKey="referenceEmail"
                    validation={validationDictionary["email"]}
                />
                <StepFormTextField
                    onChange={event => onInputChange(`referenceFaxNumber`, event)}
                    inputKey="referencefaxNumber"
                    label="Fax Number"
                    placeholder="Enter Fax Number"
                    autoComplete="tel"
                    type="tel"
                    variant="small"
                    valueOverride={tradeReference.referenceFaxNumber.value}
                    validation={validationDictionary["faxNumber"]}
                />
                <TwoButtonModal
                    messageText="Are you sure you want to delete this trade reference?"
                    onSubmit={handleDeleteClick}
                    onClose={() => setIsModalOpen(false)}
                    onCancel={() => setIsModalOpen(false)}
                    cancelButtonText="Cancel"
                    submitButtonText="Confirm"
                    headlineText="Delete Trade Reference"
                    modalIsOpen={isModalOpen}
                />
            </CardComponent>
        </FadeIn>
    );
};

export default CreditFormTradeReference;
